// src/modules/pos/pages/PosTerminalPage.tsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Divider,
  Card,
  CardHeader,
  CardContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Snackbar,
  Alert,
  Tabs,
  Tab,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import PersonIcon from "@mui/icons-material/Person";
import api from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";

// Main POS Terminal UI
export default function PosTerminalPage() {
  const { token } = useAuth();
  const navigate = useNavigate();

  // POS state
  const [products, setProducts] = useState<any[]>([]);
  const [services, setServices] = useState<any[]>([]);
  const [cart, setCart] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [walkIn, setWalkIn] = useState({ name: "", phone: "" });
  const [clientId, setClientId] = useState<string>("");
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState<0 | 1>(0); // 0=Products,1=Services
  const [payments, setPayments] = useState<{ method: string; amount: number }[]>([]);
  const [paymentInput, setPaymentInput] = useState({ method: "Cash", amount: 0 });
  const [error, setError] = useState<string | null>(null);

  // Load products/services/clients
  useEffect(() => {
    api
      .get("/inventory", { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setProducts(res.data))
      .catch(() => setProducts([]));
    // Optional services list; backend may not expose this yet
    api
      .get("/services", { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setServices(res.data))
      .catch(() => setServices([]));
    api
      .get("/clients", { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setClients(res.data))
      .catch(() => setClients([]));
  }, [token]);

  // Cart logic
  const addToCart = (item: any) => {
    setCart((prev) => {
      const exist = prev.find((c) => c._id === item._id && c.type === item.type);
      if (exist) return prev.map(c => c._id === item._id && c.type === item.type ? { ...c, qty: c.qty + 1 } : c);
      return [...prev, { ...item, qty: 1, type: item.type || "product" }];
    });
  };
  const updateQty = (idx: number, qty: number) => setCart((prev) => prev.map((c, i) => i === idx ? { ...c, qty } : c));
  const removeItem = (idx: number) => setCart((prev) => prev.filter((_, i) => i !== idx));

  // Calculate totals
  const subtotal = useMemo(() => cart.reduce((sum, i) => sum + (i.sellingPrice || i.price || 0) * i.qty, 0), [cart]);
  const tax = useMemo(() => cart.reduce((sum, i) => sum + ((i.taxRate || 0) * (i.sellingPrice || i.price || 0) * i.qty) / 100, 0), [cart]);
  const grandTotal = useMemo(() => subtotal + tax, [subtotal, tax]);

  // Payment
  const addPayment = () => {
    if (!paymentInput.amount || paymentInput.amount <= 0) return;
    setPayments((p) => [...p, paymentInput]);
    setPaymentInput({ method: paymentInput.method, amount: 0 });
  };
  const removePayment = (idx: number) => setPayments((prev) => prev.filter((_, i) => i !== idx));
  const totalPaid = useMemo(() => payments.reduce((s, p) => s + (Number(p.amount) || 0), 0), [payments]);

  // Checkout logic
  const handleCheckout = async () => {
    if (cart.length === 0) return setError("No items in cart");
    const saleData = {
      items: cart.map((c) => ({
        itemId: c._id,
        name: c.name,
        quantity: c.qty,
        sellingPrice: c.sellingPrice || c.price,
        taxRate: c.taxRate || 0,
      })),
      payments,
      clientId: clientId || undefined,
      walkInCustomer: !clientId ? walkIn : undefined,
      notes: "",
    };
    try {
      const res = await api.post("/pos", saleData, { headers: { Authorization: `Bearer ${token}` } });
      navigate(`/pos/sale/${res.data._id}`);
    } catch (e: any) {
      setError(e?.response?.data?.message || "Failed to complete sale");
    }
  };

  // UI
  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Grid container spacing={2}>
        {/* Catalog */}
        <Grid item xs={12} md={6} lg={7}>
          <Card sx={{ height: "100%" }}>
            <CardHeader title="Catalog" subheader="Search and add items to cart" />
            <CardContent>
              <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
                <TextField
                  fullWidth
                  placeholder="Search products/services"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <Tabs value={tab} onChange={(_, v) => setTab(v)}>
                  <Tab label="Products" />
                  <Tab label="Services" />
                </Tabs>
              </Box>
              <Grid container spacing={2}>
                {(tab === 0 ? products : services)
                  .filter((item: any) => item.name?.toLowerCase().includes(search.toLowerCase()))
                  .slice(0, 120)
                  .map((item: any) => (
                    <Grid item xs={6} md={4} lg={3} key={`${tab}-${item._id || item.name}`}>
                      <Paper
                        variant="outlined"
                        sx={{ p: 1.5, borderRadius: 2, cursor: "pointer", '&:hover': { boxShadow: 3 } }}
                        onClick={() => addToCart(item)}
                      >
                        <Typography fontWeight={600} noWrap>{item.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          ${(item.sellingPrice || item.price || 0).toFixed(2)}
                        </Typography>
                        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                          <IconButton color="primary" size="small"><AddShoppingCartIcon fontSize="small" /></IconButton>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Right side: Customer, Cart, Payments, Summary */}
        <Grid item xs={12} md={6} lg={5}>
          <Grid container spacing={2}>
            {/* Customer */}
            <Grid item xs={12}>
              <Card>
                <CardHeader title="Customer" avatar={<PersonIcon />} />
                <CardContent>
                  <Select fullWidth value={clientId} onChange={(e) => setClientId(e.target.value as string)} displayEmpty>
                    <MenuItem value="">Walk-in</MenuItem>
                    {clients.map((c) => (
                      <MenuItem key={c._id} value={c._id}>{c.fullName}</MenuItem>
                    ))}
                  </Select>
                  {!clientId && (
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5, mt: 1.5 }}>
                      <TextField size="small" label="Name" value={walkIn.name} onChange={(e) => setWalkIn({ ...walkIn, name: e.target.value })} />
                      <TextField size="small" label="Phone" value={walkIn.phone} onChange={(e) => setWalkIn({ ...walkIn, phone: e.target.value })} />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Cart */}
            <Grid item xs={12}>
              <Card>
                <CardHeader title="Cart" />
                <CardContent>
                  {cart.length === 0 ? (
                    <Typography color="text.secondary">No items yet. Add from the catalog.</Typography>
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Item</TableCell>
                          <TableCell align="right">Unit</TableCell>
                          <TableCell align="right">Qty</TableCell>
                          <TableCell align="right">Line</TableCell>
                          <TableCell align="right">Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {cart.map((item, idx) => {
                          const price = item.sellingPrice || item.price || 0;
                          const line = price * item.qty * (1 + (item.taxRate || 0) / 100);
                          return (
                            <TableRow key={`${item._id || item.name}-${idx}`}>
                              <TableCell>
                                <Typography fontWeight={600}>{item.name}</Typography>
                                <Typography variant="caption" color="text.secondary">{item.type || "item"}</Typography>
                              </TableCell>
                              <TableCell align="right">${price.toFixed(2)}</TableCell>
                              <TableCell align="right" width={110}>
                                <TextField
                                  type="number"
                                  size="small"
                                  value={item.qty}
                                  onChange={(e) => updateQty(idx, Math.max(1, Number(e.target.value)))}
                                  inputProps={{ min: 1 }}
                                />
                              </TableCell>
                              <TableCell align="right">${line.toFixed(2)}</TableCell>
                              <TableCell align="right">
                                <IconButton color="error" onClick={() => removeItem(idx)} size="small"><DeleteIcon /></IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Payments */}
            <Grid item xs={12}>
              <Card>
                <CardHeader title="Payments" />
                <CardContent>
                  <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
                    <Select
                      size="small"
                      value={paymentInput.method}
                      onChange={(e) => setPaymentInput((p) => ({ ...p, method: e.target.value as string }))}
                      sx={{ minWidth: 160 }}
                    >
                      <MenuItem value="Cash">Cash</MenuItem>
                      <MenuItem value="Card">Card</MenuItem>
                      <MenuItem value="Bank">Bank</MenuItem>
                      <MenuItem value="EcoCash">EcoCash</MenuItem>
                      <MenuItem value="EasyMoney">EasyMoney</MenuItem>
                      <MenuItem value="Innbucks">Innbucks</MenuItem>
                      <MenuItem value="O'MARI">O'MARI</MenuItem>
                    </Select>
                    <TextField
                      type="number"
                      size="small"
                      label="Amount"
                      value={paymentInput.amount}
                      onChange={(e) => setPaymentInput((p) => ({ ...p, amount: Number(e.target.value) }))}
                    />
                    <Button variant="contained" onClick={addPayment}>Add</Button>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1.5 }}>
                    {payments.map((p, i) => (
                      <Chip key={i} label={`${p.method}: $${Number(p.amount).toFixed(2)}`} onDelete={() => removePayment(i)} />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Summary */}
            <Grid item xs={12}>
              <Card>
                <CardHeader title="Summary" />
                <CardContent>
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr auto", rowGap: 0.5 }}>
                    <Typography color="text.secondary">Subtotal</Typography>
                    <Typography fontWeight={600}>${subtotal.toFixed(2)}</Typography>
                    <Typography color="text.secondary">Tax</Typography>
                    <Typography fontWeight={600}>${tax.toFixed(2)}</Typography>
                    <Divider sx={{ gridColumn: "1 / span 2", my: 1 }} />
                    <Typography fontWeight={700}>Total</Typography>
                    <Typography fontWeight={700}>${grandTotal.toFixed(2)}</Typography>
                    <Typography color="text.secondary">Paid</Typography>
                    <Typography fontWeight={600}>${totalPaid.toFixed(2)}</Typography>
                    <Typography color="text.secondary">Due</Typography>
                    <Typography fontWeight={600}>${Math.max(grandTotal - totalPaid, 0).toFixed(2)}</Typography>
                  </Box>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    sx={{ mt: 2 }}
                    onClick={handleCheckout}
                    disabled={cart.length === 0}
                  >
                    Complete Sale & Print
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
    </Box>
  );
}
